import { PROJECT_CONFLICTS_DATASET_SLUG } from '@civilgrid/utils/src/intelligence/index';
import { DEFAULT_VISIBLE_LAYERS } from '@civilgrid/utils/src/layerConfig/defaultLayers';
import { cloneDeep, isEmpty, isUndefined } from 'lodash';
import { proxy, subscribe } from 'valtio';
import { proxySet, subscribeKey } from 'valtio/utils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { getPref, Prefs, setPref } from '~/common/prefs';
import type { TOCItem, TOCParent, TOC as TOCType } from '~/common/types';
import { CUSTOM_DATA_LAYER_PREFIX } from '~/pages/projectDetails/map/customDataLayers.helpers';
import { GEOREFERENCED_MAP_LAYER_PREFIX } from '~/pages/projectDetails/map/georeferencedMapLayers.helpers';
import { getCustomLayersTilesConfig, getTilesConfig } from '../api/layers2';
// Pure data + types live in ./layers.data; re-exported for back-compat.
import {
  DEFAULT_METADATA_IDS,
  type Fields,
  type Layer,
  type Legend,
  type MapboxRasterSourceConfig,
  type MapboxSourceConfig,
  type MapboxVectorSourceConfig,
  type TilesConfig,
  type VectorLayer,
} from './layers.data';
import {
  allSpecialDatasetLayerIds,
  allSpecialDatasetNames,
  getSpecialDatasetConfig,
  type SpecialDatasetConfig,
} from './specialDatasets';
// utility commands presets
import {
  mergeCustomLayersRoot,
  TOC,
  UtilityLineLayers,
  UtilityPointAndPolygonLayers,
  UtilitySectionLabels,
  UtilityServiceLayers,
} from './TOCLayers';

export {
  DEFAULT_METADATA_IDS,
  type Fields,
  type Layer,
  type Legend,
  type MapboxRasterSourceConfig,
  type MapboxSourceConfig,
  type MapboxVectorSourceConfig,
  type TilesConfig,
  type VectorLayer,
};

export function isTOCParent(item: any): item is TOCParent {
  return item.children;
}

export const PINNED_LAYERS = 'Bookmarked Layers';

// sectionId for the runtime "Custom Data" TOC group (project-scoped WMTS rasters).
export const CUSTOM_DATA_SECTION_ID = 'custom-data';

const SESSION_LINKED_ORG_VISIBILITY_KEY = 'CG.SESSION.LINKED_ORG_VISIBILITY';
const SESSION_LINKED_ORGS_SECTION_EXPANDED_KEY = 'CG.SESSION.LINKED_ORGS_SECTION_EXPANDED';

// The store is instantiated at module scope, so importing this module must not require browser
// globals (vitest runs in a plain `node` environment). Outside the browser the linked-org
// persistence is a no-op.
const safeSessionStorage = typeof sessionStorage === 'undefined' ? null : sessionStorage;

// Module-level (not on the proxied store) so valtio never wraps the in-flight promise.
let tilesConfigFetch: Promise<void> | undefined;

export class LayerStore {
  tilesConfig?: TilesConfig;
  customLayersTilesConfig?: TilesConfig;
  visibleLayerIds: Set<string>;
  visibleMetadataIds: Set<string>;
  isTOCVisible = false;
  isMetadataPickerVisible = false;
  isGeoreferencedMapsPanelVisible = false;
  isLegendVisible = false;
  activeLayerIds: string[] = [];
  isObjectsVisible = false;
  TOCLayers: TOCType = cloneDeep(TOC);
  // Project-scoped "Custom Data" children, mirrored by setCustomDataTOCGroup. Dedicated field
  // (not a TOCLayers splice) so the picker's overlay section survives setTOCLayersConfig
  // rebuilds. Session-only — never persisted. The fallback `TOCLayers` picker reads the splice.
  customDataTOCGroup: TOCItem[] = [];
  // Project-scoped geo-referenced maps for the Geo-referenced Maps panel. Session-only.
  georeferencedMapsTOCGroup: TOCItem[] = [];
  expandedSections: Set<string> = proxySet<string>();
  pinnedLayers: Map<string, TOCItem> = new Map();
  projectLayer: boolean = true;
  pinnedLayerIds: Set<string> = proxySet<string>();
  activeSpecialDatasets: Set<string> = proxySet();
  visibleLinkedOrganizationIds: Set<string> = proxySet<string>();
  linkedOrgsSectionExpanded: boolean = false;
  // True while a download form is mounted. Pauses the three visibility persistence
  // subscriptions (LAST_USED_LAYERS, linked-org sessionStorage, and the per-map
  // preferred-layers key) so download-mode wipes never overwrite the user's saved layers.
  pausePersistence: boolean = false;

  // Leaf ids an active tab is forcing visible (Land Rights / Zoning force parcels,
  // Intelligence forces its per-section ids). While an id is here the picker renders it
  // checked + disabled, and no picker edit or wholesale clear may remove it from the render
  // set — only the owning tab, via store2/tabLayerLocks.ts, which is the sole writer.
  lockedLeafIds: Set<string> = proxySet<string>();

  utilityLineLayers: string[] = [...UtilityLineLayers];
  utilityPointsAndPolygonLayers: string[] = [...UtilityPointAndPolygonLayers];
  utilityServiceLayers: string[] = [...UtilityServiceLayers];

  // Stops the three legacy localStorage writebacks below — the per-user layer config keeps no
  // localStorage cache. Set at startup (`index.tsx`) and held by the layerConfig store
  // (`store2/layerConfig.ts`), the persistence-policy seam for this store.
  suppressLocalPersistence = false;

  // While the new-download form owns map visibility (setup()..reset()), CustomDataLayers must not
  // decide the visibility of newly-seen custom-data layers — there the picker selection is the
  // only source of truth for what renders on the preview (CG-694).
  suppressCustomDataDefaultOn = false;

  constructor() {
    this.visibleLayerIds = proxySet<string>();
    this.visibleMetadataIds = proxySet<string>();

    const lastUsedLayers = getPref(Prefs.LAST_USED_LAYERS);
    const lastUsedMetadataFields = getPref(Prefs.LAST_USED_METADATA_FIELDS);

    if (lastUsedLayers) {
      lastUsedLayers.split(',').forEach((layer) => {
        this.visibleLayerIds.add(layer);
      });
    } else {
      DEFAULT_VISIBLE_LAYERS.forEach((layerId) => {
        this.visibleLayerIds.add(layerId);
      });
    }

    if (lastUsedMetadataFields) {
      lastUsedMetadataFields.split(',').forEach((field) => {
        this.visibleMetadataIds.add(field);
      });
    } else {
      DEFAULT_METADATA_IDS.forEach((id) => {
        this.visibleMetadataIds.add(id);
      });
    }

    const lastExpandedSections = getPref(Prefs.EXPANDED_SECTIONS);

    if (lastExpandedSections) {
      try {
        const parsed = JSON.parse(lastExpandedSections);
        parsed.forEach((section: string) => {
          this.expandedSections.add(section);
        });
      } catch {
        this.toggleAllSections(true);
      }
    } else {
      this.toggleAllSections(true);
    }

    const pinnedLayerIds = getPref(Prefs.PINNED_LAYER_IDS);

    if (pinnedLayerIds) {
      try {
        const parsed = JSON.parse(pinnedLayerIds);
        parsed.forEach((layerId: string) => {
          this.addPinnedLayer(layerId);
        });
      } catch {
        console.error('Failed to parse bookmarked layers, resetting to defaults');
        this.pinnedLayerIds.clear();
        this.pinnedLayers.clear();
      }
    }

    const savedLinkedOrgIds = safeSessionStorage?.getItem(SESSION_LINKED_ORG_VISIBILITY_KEY);
    if (savedLinkedOrgIds) {
      try {
        const parsed = JSON.parse(savedLinkedOrgIds);
        parsed.forEach((id: string) => {
          this.visibleLinkedOrganizationIds.add(id);
        });
      } catch {
        // stay empty
      }
    }

    const savedLinkedOrgsExpanded = safeSessionStorage?.getItem(SESSION_LINKED_ORGS_SECTION_EXPANDED_KEY);
    if (savedLinkedOrgsExpanded) {
      try {
        this.linkedOrgsSectionExpanded = JSON.parse(savedLinkedOrgsExpanded);
      } catch {
        // stay false
      }
    }

    subscribe(this.visibleLinkedOrganizationIds, () => {
      if (this.pausePersistence) return; // download mode must not poison saved visibility
      safeSessionStorage?.setItem(
        SESSION_LINKED_ORG_VISIBILITY_KEY,
        JSON.stringify(Array.from(this.visibleLinkedOrganizationIds)),
      );
    });

    subscribe(this.visibleLayerIds, () => {
      if (this.pausePersistence) return; // download mode must not poison saved visibility
      capturePosthogEvent(POSTHOG_EVENTS.mapLayersInView, {
        layer_ids: Array.from(this.visibleLayerIds).join(','),
      });
      // Backend-managed mode keeps no localStorage cache for this feature; the
      // debounced PUT sync is the only sink. The analytics event above still fires.
      if (!this.suppressLocalPersistence) {
        setPref(Prefs.LAST_USED_LAYERS, Array.from(this.visibleLayerIds).join(','));
      }
    });

    subscribe(this.visibleMetadataIds, () => {
      capturePosthogEvent(POSTHOG_EVENTS.mapMetadataInView, {
        layer_ids: Array.from(this.visibleMetadataIds).join(','),
      });
      setPref(Prefs.LAST_USED_METADATA_FIELDS, Array.from(this.visibleMetadataIds).join(','));
    });

    subscribe(this.expandedSections, () => {
      if (this.suppressLocalPersistence) return;
      // Custom-data and geo-referenced map keys are per-project and session-only;
      // persisting them would accumulate dead ids in this global pref forever.
      const persistable = Array.from(this.expandedSections).filter(
        (key) => !key.startsWith(CUSTOM_DATA_LAYER_PREFIX) && !key.startsWith(GEOREFERENCED_MAP_LAYER_PREFIX),
      );
      setPref(Prefs.EXPANDED_SECTIONS, JSON.stringify(persistable));
    });

    subscribe(this.pinnedLayerIds, () => {
      if (this.suppressLocalPersistence) return;
      const stringified = JSON.stringify(Array.from(this.pinnedLayerIds));
      setPref(Prefs.PINNED_LAYER_IDS, stringified);
    });
  }

  get currentSpecialDatasetLayerIds() {
    const ids = new Set<string>();

    const getLayersIds = (items: TOCType) => {
      items.forEach((item) => {
        if (this.isTOCParent(item)) {
          getLayersIds(item.children);
        } else {
          ids.add(item.id);
        }
      });
    };

    this.activeSpecialDatasets.forEach((dataset) => {
      const config = getSpecialDatasetConfig(dataset);
      if (config) {
        getLayersIds(config.toc);
      }
    });

    return ids;
  }

  /**
   * Fetch the tile-server configs once per session. Memoized so concurrent callers —
   * app bootstrap and the Layer Library page — share a single request; the
   * response is session-cached on `tilesConfig` / `customLayersTilesConfig` either way.
   */
  fetchTilesConfig(): Promise<void> {
    // A load that produced no base config (loadTilesConfig swallows the rejection) must not
    // stay memoized, or a transient bootstrap failure would pin an empty config for the whole
    // org session. Dropping the memo re-arms lazily: the NEXT caller refetches — no retry loop.
    tilesConfigFetch ??= this.loadTilesConfig().finally(() => {
      if (!this.tilesConfig) tilesConfigFetch = undefined;
    });
    return tilesConfigFetch;
  }

  /**
   * Clear the memoized fetch so the next `fetchTilesConfig()` refetches. Called on org
   * switch (`setCurrentOrganization`) because `customLayersTilesConfig` is org-scoped and
   * would otherwise stay pinned to the previous org's config for the session.
   */
  resetTilesConfigCache() {
    tilesConfigFetch = undefined;
  }

  private async loadTilesConfig(): Promise<void> {
    try {
      const [baseResult, customResult] = await Promise.allSettled([getTilesConfig(), getCustomLayersTilesConfig()]);

      if (baseResult.status === 'fulfilled') {
        const tilesConfig = baseResult.value;
        // fiberLitBuildings is project-scoped (from ConnectBase); backend tilesConfig may omit
        // these fields, so layer them on here for MetadataSidebar / Legend metadata.
        const fiberLitBuildings = tilesConfig.fiberLitBuildings;
        if (fiberLitBuildings) {
          tilesConfig.fiberLitBuildings = {
            ...fiberLitBuildings,
            fields: {
              ...(fiberLitBuildings.fields ?? {}),
              provider: { alias: 'Provider', type: 'string' },
              accessMedium: { alias: 'Access Medium', type: 'string' },
              address: { alias: 'Address', type: 'string' },
            },
          };
        }
        this.tilesConfig = tilesConfig;
      } else {
        throw baseResult.reason;
      }

      if (customResult.status === 'fulfilled') {
        this.customLayersTilesConfig = customResult.value;
      } else {
        console.error('Failed to fetch custom layers config', customResult.reason);
        this.customLayersTilesConfig = {};
      }
    } catch (e) {
      console.error('Failed to fetch tiles config', e);
    }
  }

  setTOCLayersConfig(opts?: { specialDatasets?: string[] }) {
    const toc = cloneDeep(TOC);
    const datasets: SpecialDatasetConfig[] = [];
    const activeSpecialDatasets: Set<string> = new Set();

    opts?.specialDatasets?.forEach((dataset) => {
      const config = getSpecialDatasetConfig(dataset);
      if (config) {
        if (dataset === PROJECT_CONFLICTS_DATASET_SLUG) {
          /**
           * The `Project Conflicts` section behaves like a special dataset
           * but it belongs in its own top-level section, not under `Customer Layers`.
           * This looks for the last of the pinned layers, then places the
           * Project Conflicts section into the TOC in its correct place, alphabetically.
           */
          const afterPinned = toc.findIndex((n) => n.label === 'Utilities') + 1; // Site Data and Utilities are pinned first
          const at = toc.findIndex((n, i) => i >= afterPinned && n.label.localeCompare(config.name) > 0);
          toc.splice(at === -1 ? toc.length : at, 0, { label: config.name, expanded: true, children: config.toc });
        } else {
          datasets.push(config);
        }
        activeSpecialDatasets.add(dataset);
      }
    });

    if (!isEmpty(datasets)) {
      const customLayers: TOCParent = {
        label: 'Custom Layers',
        expanded: true,
        children: datasets.map((d) => ({ label: d.name, expanded: true, children: d.toc })),
      };
      this.TOCLayers = mergeCustomLayersRoot(toc, customLayers);
    } else {
      this.TOCLayers = toc;
    }

    this.activeSpecialDatasets = proxySet(activeSpecialDatasets);

    // Drop pinned layers whose special dataset is no longer active. Must run after
    // activeSpecialDatasets is set above.
    const activeSpecialLayers = this.currentSpecialDatasetLayerIds;
    const pinnedLayersToAdd = new Set<string>();

    // Union both sets in case they've drifted out of sync.
    const _pinnedIds = new Set([...this.pinnedLayers.keys(), ...this.pinnedLayerIds]);
    _pinnedIds.forEach((layerId) => {
      if (allSpecialDatasetLayerIds.has(layerId)) {
        if (activeSpecialLayers.has(layerId)) {
          pinnedLayersToAdd.add(layerId);
        }
      } else {
        pinnedLayersToAdd.add(layerId);
      }
    });

    this.pinnedLayerIds.clear();
    this.pinnedLayers.clear();

    pinnedLayersToAdd.forEach((layerId) => {
      this.addPinnedLayer(layerId);
    });
  }

  // Project-scoped "Custom Data" TOC group. Built at runtime from per-project custom-data
  // rows (fetched async), not from tilesConfig, so it is injected here rather than in
  // setTOCLayersConfig. Idempotent: replaces any existing group, or removes it when empty.
  setCustomDataTOCGroup(children: TOCItem[]) {
    const label = 'Custom Data';
    const toc = this.TOCLayers.filter((item) => !(isTOCParent(item) && item.sectionId === CUSTOM_DATA_SECTION_ID));
    if (children.length > 0) {
      toc.unshift({ label, sectionId: CUSTOM_DATA_SECTION_ID, expanded: true, children });
    }
    this.TOCLayers = toc;
    // Document picker source (CustomDataTOC overlay section). The TOCLayers splice above stays
    // the fallback picker's source; this field is the one a setTOCLayersConfig rebuild can't drop.
    this.customDataTOCGroup = children;
  }

  setActiveLayerIds(activeLayerIds: string[]) {
    this.activeLayerIds = activeLayerIds;
  }

  toggleTOC(forceOpen?: boolean) {
    if (isUndefined(forceOpen)) {
      this.isTOCVisible = !this.isTOCVisible;
      if (!this.isTOCVisible) {
        this.toggleMetadataPicker(false);
      }
    } else {
      this.isTOCVisible = forceOpen;
    }
    // Both panels open in the map's top-left slot, so only one can be open.
    if (this.isTOCVisible) this.isGeoreferencedMapsPanelVisible = false;
  }

  toggleGeoreferencedMapsPanel(forceOpen?: boolean) {
    this.isGeoreferencedMapsPanelVisible = forceOpen ?? !this.isGeoreferencedMapsPanelVisible;
    if (this.isGeoreferencedMapsPanelVisible) {
      this.isTOCVisible = false;
      this.toggleMetadataPicker(false);
    }
  }

  toggleMetadataPicker(forceOpen?: boolean) {
    if (isUndefined(forceOpen)) {
      this.isMetadataPickerVisible = !this.isMetadataPickerVisible;
    } else {
      this.isMetadataPickerVisible = forceOpen;
    }
  }

  toggleLegend(forceOpen?: boolean) {
    if (isUndefined(forceOpen)) {
      this.isLegendVisible = !this.isLegendVisible;
    } else {
      this.isLegendVisible = forceOpen;
    }
  }

  toggleObjects() {
    this.isObjectsVisible = !this.isObjectsVisible;
  }

  addPinnedLayer(layerId: string) {
    if (this.pinnedLayerIds.has(layerId) && this.pinnedLayers.has(layerId)) return;

    const addToPinnedLayers = (items: TOCItem[], parent?: string) => {
      items.forEach((item) => {
        if (this.isTOCParent(item)) {
          addToPinnedLayers(item.children, item.label);
        } else {
          if (item.id === layerId) {
            let label = item.label;

            if (parent && (UtilitySectionLabels.has(parent) || allSpecialDatasetNames.has(parent))) {
              label = `${parent} / ${label}`;
            }

            this.pinnedLayers.set(item.id, {
              id: item.id,
              label,
            });
          }
        }
      });
    };

    addToPinnedLayers(this.TOCLayers);
    this.pinnedLayerIds.add(layerId);
  }

  removePinnedLayer(layerId: string) {
    this.pinnedLayerIds.delete(layerId);
    this.pinnedLayers.delete(layerId);
  }

  toggleAllSections(expanded: boolean) {
    this.expandedSections.clear();
    this.linkedOrgsSectionExpanded = expanded;

    if (!expanded) return;

    this.expandedSections.add(PINNED_LAYERS);

    const checkSection = (items: TOCItem[]) => {
      items.forEach((item) => {
        if (this.isTOCParent(item)) {
          // Key sections exactly as the picker reads them (TOC.tsx renderParent: `sectionId ||
          // label`). Label-only keying left every sectionId-carrying parent — the Custom Data
          // group and its per-upload rows — collapsed under search and expand-all (CG-957).
          this.expandedSections.add(item.sectionId || item.label);

          checkSection(item.children);
        }
      });
    };

    checkSection(this.TOCLayers);
  }

  toggleAllLayers(visible: boolean) {
    this.visibleLayerIds.clear();
    // Tab-locked ids survive a wholesale clear (picker "turn off all", isolate): the active
    // tab owns their render visibility until it unlocks them.
    this.lockedLeafIds.forEach((id) => {
      this.visibleLayerIds.add(id);
    });

    if (!visible) {
      this.visibleLinkedOrganizationIds.clear();
      return;
    }

    const checkSection = (items: TOCItem[]) => {
      items.forEach((item) => {
        if (this.isTOCParent(item)) {
          checkSection(item.children);
        } else {
          this.visibleLayerIds.add(item.id);
        }
      });
    };

    checkSection(this.TOCLayers);
  }

  toggleAllSectionLayers(items: TOCItem[], visible: boolean) {
    items.forEach((item) => {
      if (this.isTOCParent(item)) {
        this.toggleAllSectionLayers(item.children, visible);
      } else {
        if (visible) {
          this.visibleLayerIds.add(item.id);
        } else {
          this.visibleLayerIds.delete(item.id);
        }
      }
    });
  }

  isTOCParent(item: any): item is TOCParent {
    return item.children;
  }

  isolateLayer(layerId: string) {
    this.toggleAllLayers(false);

    // this doesn't work without setTimeout, please keep.
    setTimeout(() => {
      this.visibleLayerIds.add(layerId);
    }, 0);
  }

  clearVisibleLinkedOrganizationIds() {
    this.visibleLinkedOrganizationIds.clear();
  }

  toggleLinkedOrgSection() {
    this.linkedOrgsSectionExpanded = !this.linkedOrgsSectionExpanded;
  }

  // The project's geo-referenced maps as agency → file → page items, built at runtime; empty clears them.
  setGeoreferencedMapsTOCGroup(children: TOCItem[]) {
    this.georeferencedMapsTOCGroup = children;
    if (children.length === 0) this.isGeoreferencedMapsPanelVisible = false;
  }
}

const layerStore = proxy(new LayerStore());

subscribeKey(layerStore, 'linkedOrgsSectionExpanded', (value) => {
  safeSessionStorage?.setItem(SESSION_LINKED_ORGS_SECTION_EXPANDED_KEY, JSON.stringify(value));
});

export default layerStore;
